import { Theme } from '@monorepo/relayably/theme'
import { LinearGradient } from 'expo-linear-gradient'
import { Image, Platform, SafeAreaView, ScrollView, StyleSheet, Text, useWindowDimensions, View } from 'react-native'

const hexToRgba = (hex: string | undefined, alpha: number) => {
  if (!hex) {
    return `rgba(255, 255, 255, ${alpha})`
  }

  if (!hex.startsWith('#')) {
    return hex
  }

  const normalized = hex.replace('#', '')
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : normalized
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const BrandLogo = ({ theme }: { theme: Theme }) => {
  const styles = createStyles(theme, 1200, false)

  return (
    <View style={styles.logoContainer}>
      <View style={styles.logoGlow} />
      <View style={styles.logoBase}>
        <Image source={require('../../../../../assets/relayably/logo-256.png')} style={styles.logoImage} />
      </View>
      <Text style={styles.logoText}>
        Relay<Text style={{ color: theme.colors.primary }}>ably</Text>
      </Text>
    </View>
  )
}

export const AuthLayout = ({
  children,
  theme,
  title,
  subtitle,
}: {
  children: React.ReactNode
  theme: Theme
  title: string
  subtitle: string
}) => {
  const { width } = useWindowDimensions()
  const stacked = width < 960
  const styles = createStyles(theme, width, stacked)

  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps='handled'>
        <View style={styles.shell}>
          {!stacked && (
            <View style={styles.brandingSide}>
              <BrandLogo theme={theme} />
              <View style={styles.brandingCopyWrap}>
                <Text style={styles.brandingHeadline}>
                  Your communication finally got{'\n'}
                  <Text style={{ color: theme.colors.primary }}>an operating system.</Text>
                </Text>
                <Text style={styles.brandingBody}>
                  Calls, messages, spam, enquiries, interruptions. Relayably helps you stay responsive without letting it take over your day.
                </Text>
              </View>
              <Text style={styles.brandingFooter}>© 2026 RELAYABLY ORCHESTRATION SYSTEMS</Text>
              <LinearGradient
                colors={[hexToRgba(theme.colors.primary, 0.22), hexToRgba(theme.colors.secondary, 0.1), 'transparent']}
                end={{ x: 1, y: 1 }}
                start={{ x: 0, y: 0 }}
                style={styles.brandingGlowLarge}
              />
            </View>
          )}

          <View style={styles.formSide}>
            {stacked ? <BrandLogo theme={theme} /> : null}
            <Text style={styles.formTitle}>{title}</Text>
            <Text style={styles.formSubtitle}>{subtitle}</Text>
            <View style={styles.formCard}>{children}</View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}

const createStyles = (theme: Theme, width: number, stacked: boolean) =>
  StyleSheet.create({
    screen: {
      flex: 1,
    },
    scrollContent: {
      flexGrow: 1,
    },
    shell: {
      backgroundColor: theme.colors.background,
      flexDirection: stacked ? 'column' : 'row',
      minHeight: '100%',
      overflow: 'hidden',
      width: '100%',
    },
    brandingSide: {
      backgroundColor: theme.dark ? '#000000' : theme.colors.surface,
      borderRightColor: stacked ? 'transparent' : theme.colors.border,
      borderRightWidth: stacked ? 0 : 1,
      borderBottomColor: stacked ? theme.colors.border : 'transparent',
      borderBottomWidth: stacked ? 1 : 0,
      justifyContent: 'space-between',
      minHeight: stacked ? 360 : '100%',
      overflow: 'hidden',
      padding: stacked ? 24 : 42,
      position: 'relative',
      width: stacked ? '100%' : Math.min(520, width * 0.42),
    },
    brandingCopyWrap: {
      marginTop: 48,
      maxWidth: 420,
    },
    brandingHeadline: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: stacked ? 30 : 42,
      lineHeight: stacked ? 36 : 48,
    },
    brandingBody: {
      color: theme.colors.inactive,
      fontSize: 16,
      lineHeight: 26,
      marginTop: 18,
    },
    brandingFooter: {
      color: theme.colors.inactive,
      fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
      fontSize: 12,
      marginTop: 40,
    },
    brandingGlowLarge: {
      ...StyleSheet.absoluteFillObject,
      opacity: 1,
      pointerEvents: 'none',
    },
    brandingGlowBottom: {
      bottom: -40,
      height: 280,
      left: -40,
      opacity: 0.9,
      pointerEvents: 'none',
      position: 'absolute',
      width: 360,
    },
    formSide: {
      alignItems: 'center',
      backgroundColor: theme.colors.background,
      flex: 1,
      justifyContent: 'center',
      padding: stacked ? 24 : 42,
    },
    formTitle: {
      textAlign: 'center',
      alignSelf: 'center',
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 34,
      lineHeight: 40,
      marginTop: stacked ? 24 : 0,
      maxWidth: 420,
      width: '100%',
    },
    formSubtitle: {
      textAlign: 'center',
      alignSelf: 'center',
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
      marginTop: 12,
      maxWidth: 420,
      width: '100%',
    },
    formCard: {
      marginTop: 32,
      maxWidth: 420,
      width: '100%',
    },
    logoContainer: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 14,
    },
    logoGlow: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.2),
      borderRadius: 999,
      height: 44,
      left: -2,
      position: 'absolute',
      width: 44,
    },
    logoBase: {
      alignItems: 'center',
      backgroundColor: theme.colors.card,
      borderColor: theme.colors.border,
      borderRadius: 12,
      borderWidth: 1,
      height: 48,
      justifyContent: 'center',
      overflow: 'hidden',
      width: 48,
    },
    logoImage: {
      height: 48,
      width: 48,
    },
    logoText: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 24,
      fontWeight: '700',
    },
  })
